import { useEffect, useState } from 'react';
import { Outlet, useParams } from 'react-router-dom';

import { Box } from '@mui/material';

import ClientsTableDataRow from './ClientsTableDataRow';

import { getClientsList } from '../../api/clientsApi/clientsApi';
import TableContainer from '../../components/Table/TableContainer';
import Loader from '../../components/Loader';
import { getFirmClientsListTableLabels } from '../../constants/tableLabels';
import usePagination from '../../hooks/usePagination';
import { ActiveSortField, ClientData, FilterTypes } from '../../types';
import NoDataText from '../../components/NoDataText';
import OnboardingStatusLegend from '../../components/OnboardingStatusLegend';
import { downloadCsvorExcel } from '../../helpers/downloadCsv';
import TableActions from '../../components/Table/TableActions';
import TableFilters from '../../components/TableFilters/TableFilters';
import useSnackBar from '../../hooks/useSnackBar';
import { handleSortTableData } from '../../helpers/handleSortTableData';
import { useHasPermission } from '../../hooks/useHasPermission';
// import { CLIENTS_STATUS_FILTER_OPTIONS } from '../../components/TableFilters/constants';
import { ONBOARDING_STATUS_FILTER_OPTIONS } from '../../components/TableFilters/constants';

// Temporarily hidden; revisit later
const SHOW_ONBOARDING_STATUS_FILTER = false;

const ClientsView = () => {
  const params = useParams();
  const firmId = localStorage.getItem('firmId');

  const { pageNumber, rowsPerPage, setTotalCount } = usePagination();
  const { setAlert } = useSnackBar();
  const canCreate = useHasPermission('Clients', 'Create');

  const [clientsList, setClientsList] = useState<ClientData[]>([]);
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [showFilters, setShowFilters] = useState<boolean>(true);

  const showOnboardingDetails = localStorage.getItem('showUserOnboardingDetails') === 'true';
  const hasParams =!!Object.entries(params).length;

  const [filters, setFilters] = useState<FilterTypes>({
    firmId: firmId || '',
    clientId: '',
    defendantId: '',

  });
  const [match, setMatch] = useState<string>('');
  const [activeSortField, setActiveSortField] = useState<ActiveSortField | null>(null);
  const sortableFields = {
    name: {
      order: '',
      fieldName: 'name',
    },
     updatedAt: {
      order: '',
      fieldName: 'updatedAt',
    },
    // createdAt: {
    //   order: '',
    //   fieldName: 'createdAt',
    // },
  };

  useEffect(() => {
    getClientsList({
      page: pageNumber,
      size: rowsPerPage,
      count: pageNumber === 1,
      match: match,
      filters: { ...filters, ...(hasParams && params) },
      activeSortField,
    }).then((res) => {
      if (res.data) {
        pageNumber === 1 && setTotalCount?.(res.data.totalCount || 0);
        setClientsList(res.data.resultSet);
        setIsDataLoading(false);
      }
    });
  }, [pageNumber, rowsPerPage, filters, match, activeSortField]);

  const exportList = () => {
    downloadCsvorExcel('/v1/clients/export', 'POST', 'Clients_List','csv', {
      page: pageNumber,
      size: rowsPerPage,
      count: true,
      match: match,
      filters: filters,
    }).then((res) => {
      if (res?.data) {
        setAlert({
          message: 'Clients exported successfully.',
          type: 'success',
        });
      }
    });
  };

  return (
    <>
      {isDataLoading && !clientsList.length ? (
        <Box position="absolute" top="50%" left="50%">
          <Loader colorType="warning" />
        </Box>
      ) : (
        <>
          <TableActions
            hasParams={hasParams}
            createLink={canCreate ? '/clients/create-client' : undefined}
            exportList={exportList}
            match={match}
            showFilters={showFilters}
            setShowFilters={setShowFilters}
            setMatch={setMatch}
          />

          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '24px' }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {showFilters && (
                <TableFilters
                  filters={filters}
                  setFilters={setFilters}
                  setMatch={setMatch}
                  timeFilter={'Created'}
                  match={match}
                  onboardingStatus={SHOW_ONBOARDING_STATUS_FILTER ? ONBOARDING_STATUS_FILTER_OPTIONS : undefined}
                  // statusFilter={CLIENTS_STATUS_FILTER_OPTIONS}
                />
              )}
            </Box>
            {showOnboardingDetails && <OnboardingStatusLegend />}
          </Box>

          {!isDataLoading && clientsList.length === 0 ? (
            <NoDataText />
          ) : (
            <TableContainer
              headerLabels={getFirmClientsListTableLabels(showOnboardingDetails)}
              sortableFields={sortableFields}
              activeSortField={activeSortField}
              setActiveSortField={setActiveSortField}
              handleSortTableData={handleSortTableData}
            >
              <ClientsTableDataRow tableData={clientsList} />
            </TableContainer>
          )}
        </>
      )}
      <Outlet />
    </>
  );
};

export default ClientsView;